<template>
  <div>
    <div
      class="mb-3"
      v-for="(field, index) in shipmentFieldsConfiguration"
      :key="index"
    >
      <NewTextInput
        v-if="field.type === 'input'"
        :name="field.name"
        :label="t(field.label)"
        :placeholder="t(field.placeholder)"
        v-model:value="form.values[field.name]"
        :error="!!form.errors[field.name] && form.touched[field.name]"
        :errorMessage="form.errors[field.name]"
        :highlight="!!form.values[field.name]"
        @onBlur="form.handleBlur"
        @input="form.handleChange"
        @onFocus="form.handleFocus"
        :errorIcon="false"
        :maxlength="field.maxLength"
        :minlength="field.minLength"
      />
      <NewAutocomplete
        v-if="field.type === 'select' && field.name === 'cityField'"
        :label="t(field.label)"
        :placeholder="t(field.placeholder)"
        :modelValue="getModelValue(field)"
        :options="getOptions(field.name)"
        :error="cityError !== null"
        :errorMessage="cityError"
        @update:modelValue="
          (value) => {
            setModelValue(field, value);
          }
        "
        :fill="true"
        :highlight="!!getModelValue(field)"
        :selectedOption="selectedOption"
        @onFocus="clearCityError"
        @onBlur="isValidCity"
        @optionSelected="isValidCity"
        @input="clearCityError"
      />
      <NewAutocomplete
        v-if="field.type === 'select' && field.name === 'neighborhoodField'"
        :label="t(field.label)"
        :placeholder="t(field.placeholder)"
        :modelValue="form.values[field.name]"
        :options="getOptions(field.name)"
        :error="!!neighborhoodError"
        :errorMessage="neighborhoodError"
        :name="field.name"
        @update:modelValue="form.values[field.name] = $event"
        :fill="true"
        :highlight="cityValidationPassed && !!form.values[field.name]"
        :selectedOption="selectedOption"
        @onFocus="
          () => form.handleFocus({ target: { name: 'neighborhoodField' } })
        "
        @onBlur="
          () => form.handleBlur({ target: { name: 'neighborhoodField' } })
        "
        @input="
          () => {
            form.handleChange({
              target: {
                name: 'neighborhoodField',
                value: form.values['neighborhoodField'],
              },
            });
            form.handleBlur({ target: { name: 'neighborhoodField' } });
          }
        "
      />
      <NewTextInput
        v-if="field.type === 'inputNumber'"
        :name="field.name"
        :label="t(field.label)"
        :placeholder="t(field.placeholder)"
        :maxlength="field.maxLength"
        :minlength="field.minLength"
        :type="'number'"
        v-model:value="form.values[field.name]"
        :error="!!form.errors[field.name] && form.touched[field.name]"
        :errorMessage="form.errors[field.name]"
        :highlight="!!form.values[field.name]"
        @onBlur="form.handleBlur"
        @input="form.handleChange"
        @onFocus="form.handleFocus"
        :errorIcon="false"
      />
    </div>
    <div
      class="pt-1"
      :class="{
        'button-active': form.isValid.value,
        'floating-btn': isFromApp,
        'px-3': isFromApp,
      }"
    >
      <Button
        id="btn-continue"
        class="text-start w-100"
        @click="form.handleSubmit"
        :disabled="!form.isValid.value || cityError !== null"
      >
        <div class="text-white text-center my-auto fw-bold btn-text">
          <span>{{ t("ACCEPT") }}</span>
        </div>
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick, inject } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import NewTextInput from "@/apps/credit-line/components/NewTextInput.vue";
import NewAutocomplete from "@/apps/credit-line/components/CreditCard/ShippingAddress/forms/NewAutocomplete.vue";
import Button from "@/apps/credit-line/components/Button.vue";
import { useForm } from "@/apps/credit-line/components/CreditCard/ShippingAddress/composables/useForms";
import { useCityField } from "@/apps/credit-line/components/CreditCard/ShippingAddress/composables/useCityField";
import { useZipcodeAutocomplete } from "@/apps/credit-line/hooks/useZipcodeAutocomplete";

const { t } = useI18n();

const emit = defineEmits(["goToConfirmClick", "enterNewAddress"]);

const neighborhoodError = ref<string | null>(null);
const autocompleteCountryCode = inject("autocompleteCountryCode");
const hasZipcodeCoverage = ref<boolean>(true); // Track zipcode coverage status
const zipcodeError = computed<string | null>(() => {
  if (!hasZipcodeCoverage.value) {
    return t("PAYJOY_CARD_VIEWS.ADDRESS_FORM_NO_COVERAGE");
  }
  return null;
});

type ShipmentFieldConfig = {
  name: string;
  label: string;
  placeholder: string;
  type: string;
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  pattern?: string;
};

const shipmentFieldsConfiguration = ref<ShipmentFieldConfig[]>([]);

const props = defineProps<{
  shipmentFieldsConfiguration: Array<ShipmentFieldConfig>;
  isFromApp?: boolean;
  deviceTag: string;
  streetName?: string;
  postalCodeField?: string;
  neighborhoodField?: string;
  cityField?: string;
  regionField?: string;
  citiesCoverage?: Array<{ city: string; region: string; isCovered: string }>;
  shouldUseCityComposable?: boolean;
  shouldUseZipcodeAutocomplete?: boolean;
}>();

// Sync prop to ref reactively
watch(
  () => props.shipmentFieldsConfiguration,
  (newVal) => {
    shipmentFieldsConfiguration.value = newVal;
  },
  { immediate: true }
);

// Zipcode autocomplete hook (for MX)
const zipcodeAutocomplete = props.shouldUseZipcodeAutocomplete
  ? useZipcodeAutocomplete(props.deviceTag)
  : null;

type ValidationFunction = (value: string) => string | null;

const isValidFormat = (value: string, fieldName: string) => {
  // Find the field configuration in the array
  const fieldConfig = shipmentFieldsConfiguration.value.find(
    (field) => field.name === fieldName
  );
  const pattern = fieldConfig?.pattern;
  const regex = pattern ? new RegExp(pattern) : /.*/;
  return regex.test(value);
};

const streetNumberValidation = (value: string) => {
  if (!hasNumber(value) && autocompleteCountryCode.value === "CO") {
    return t("PAYJOY_CARD_VIEWS.FORM_STREET_NUMBER_REQUIRED_FIELD");
  }
  return null;
};

const zipcodeCoverageValidation = (value: string) => {
  // Only validate coverage if we are using zipcode autocomplete and the zipcode has 5 digits
  if (value?.length === 5 && !hasZipcodeCoverage.value) {
    return t("PAYJOY_CARD_VIEWS.ADDRESS_FORM_NO_COVERAGE");
  }
  return null;
};

const hasNumber = (value: string) => {
  const regex = /\d/;
  return regex.test(value);
};

// Mapping of internal field names to translation names
const fieldNameToTranslationKey = (fieldName: string): string => {
  const mapping: Record<string, string> = {
    streetField: "STREET_NAME",
    neighborhoodField: "NEIGHBORHOOD",
    cityField: "CITY",
    regionField: "REGION",
    apartmentField: "APARTMENT",
    notesField: "NOTES",
    postalCodeField: "POSTAL_CODE",
  };
  return mapping[fieldName] || fieldName.replace("Field", "").toUpperCase();
};

const createFieldValidator = (
  fieldName: string,
  customValidations: ValidationFunction[] = []
) => {
  return (value: string) => {
    const trimmedValue = value?.trim() || "";
    const translationKey = fieldNameToTranslationKey(fieldName);

    if (isRequired(fieldName) && !trimmedValue) {
      return t(`PAYJOY_CARD_VIEWS.FORM_${translationKey}_REQUIRED_FIELD`);
    }

    if (!trimmedValue && !isRequired(fieldName)) {
      return null;
    }

    if (trimmedValue) {
      for (const customValidation of customValidations) {
        const customError = customValidation(trimmedValue);
        if (customError) return customError;
      }

      if (!isValidFormat(trimmedValue, fieldName)) {
        return t(`PAYJOY_CARD_VIEWS.FORM_${translationKey}_INVALID_FORMAT`);
      }
    }

    return null;
  };
};

const form = useForm({
  initialValues: {
    streetField: "",
    neighborhoodField: "",
    cityField: "",
    regionField: "",
    postalCodeField: "",
    notesField: "",
    apartmentField: "",
  },
  validationSchema: {
    streetField: createFieldValidator("streetField", [streetNumberValidation]),
    neighborhoodField: createFieldValidator("neighborhoodField"),
    cityField: createFieldValidator("cityField"),
    apartmentField: createFieldValidator("apartmentField"),
    notesField: createFieldValidator("notesField"),
    postalCodeField: createFieldValidator("postalCodeField", [
      zipcodeCoverageValidation,
    ]),
    regionField: createFieldValidator("regionField"),
  },
  onSubmit: async (_values) => {
    const formattedData = {
      street: form.values.streetField,
      apartment: form.values.apartmentField,
      city: form.values.cityField,
      region: form.values.regionField,
      postalCode: form.values.postalCodeField,
      neighborhood: form.values.neighborhoodField,
      notes: form.values.notesField,
    };
    emit("goToConfirmClick", formattedData);
  },
});

const {
  cityError,
  isValidCity,
  combinedCityRegion,
  getFilteredCities,
  getModelValue,
  setModelValue,
  selectedOption,
  cityValidationPassed,
} =
  props.shouldUseCityComposable !== false
    ? useCityField(form, props.citiesCoverage || [])
    : {
        cityError: ref(null),
        isValidCity: () => {
          cityValidationPassed.value = true;
        },
        combinedCityRegion: computed(() => ""),
        getFilteredCities: () => [],
        getModelValue: () => "",
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setModelValue: () => {},
        selectedOption: ref(null),
        cityValidationPassed: ref(true),
      };

function clearCityError() {
  cityError.value = null;
  form.validateForm();
}

// Watch zipcode field to trigger autocomplete (MX) - MOVED HERE after declaring form
if (props.shouldUseZipcodeAutocomplete && zipcodeAutocomplete) {
  watch(
    () => form.values.postalCodeField,
    async (newZipcode) => {
      // Clear coverage state when user is typing (less than 5 digits)
      if (!newZipcode || newZipcode.length < 5) {
        hasZipcodeCoverage.value = true;
        form.validateField("postalCodeField");
        return;
      }

      if (newZipcode.length >= 5) {
        try {
          form.values.regionField = "";
          const addressData = await zipcodeAutocomplete.fetchAddressByZipcode(
            newZipcode
          );
          if (addressData) {
            // Update coverage state
            hasZipcodeCoverage.value = addressData.isZoneWithCoverage;

            // Only auto-populate fields if there is coverage
            if (addressData.isZoneWithCoverage) {
              if (addressData.region) {
                form.values.cityField = addressData.region;
              }
              if (addressData.city) {
                form.values.regionField = addressData.city;
                form.validateField("regionField");
              }
            } else {
              zipcodeError.value = t(
                "PAYJOY_CARD_VIEWS.ADDRESS_FORM_NO_COVERAGE"
              );
            }

            // Mark as touched and revalidate to show error if there's no coverage
            form.touched.postalCodeField = true;
            form.validateField("postalCodeField");
          }
        } catch (error) {
          // Error fetching address by zipcode
        }
      }
    },
    { immediate: false }
  );
}

function getOptions(fieldName: string) {
  if (fieldName === "cityField") {
    return getFilteredCities();
  }
  return getFilteredNeighborhoods();
}

const getFilteredNeighborhoods = () => {
  const city = form.values.cityField;
  const region = form.values.regionField;
  const neighborhoods =
    props.citiesCoverage
      ?.filter((item) => item.city === city && item.region === region)
      .map((item) => item.neighborhood) || [];
  const uniqueNeighborhoods = [...new Set(neighborhoods)];
  // If there's only one neighborhood, auto-select it
  if (
    uniqueNeighborhoods.length === 1 &&
    form.values.neighborhoodField !== uniqueNeighborhoods[0]
  ) {
    form.values.neighborhoodField = uniqueNeighborhoods[0] as string;
    // Trigger validation and touched to activate the button
    form.handleChange({
      target: {
        name: "neighborhoodField",
        value: uniqueNeighborhoods[0],
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      preventDefault: () => {},
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      stopPropagation: () => {},
    } as unknown as Event);
    form.handleBlur({
      target: {
        name: "neighborhoodField",
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      preventDefault: () => {},
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      stopPropagation: () => {},
    } as unknown as Event);
  }
  return uniqueNeighborhoods;
};

const isRequired = (fieldName: string) => {
  // Find the field configuration in the array
  const fieldConfig = shipmentFieldsConfiguration.value.find(
    (field) => field.name === fieldName
  );
  return fieldConfig?.required ?? false;
};

watch(
  () => form.values.neighborhoodField,
  (newNeighborhood) => {
    if (newNeighborhood === "") {
      neighborhoodError.value = t(
        `PAYJOY_CARD_VIEWS.FORM_NEIGHBORHOOD_REQUIRED_FIELD`
      );
    } else {
      neighborhoodError.value = null;
    }
  }
);

watch(
  () => form.values.cityField,
  (newCity) => {
    // Reset neighborhoodField when cityField changes
    if (newCity === "") {
      form.values.neighborhoodField = "";
    }
  }
);

onMounted(async () => {
  form.resetForm();
  // Sync initial address props to local form state
  if (props.streetName) form.values.streetField = props.streetName;
  if (props.postalCodeField)
    form.values.postalCodeField = props.postalCodeField;
  if (props.neighborhoodField)
    form.values.neighborhoodField = props.neighborhoodField;
  if (props.regionField) form.values.regionField = props.regionField;
  if (props.cityField) combinedCityRegion.value = props.cityField;
  form.values.cityField = props.cityField || "";

  await nextTick();

  // Force validation of all fields to populate initial errors
  form.validateForm();
});

// Watchers to synchronize form values when props change
watch(
  () => props.streetName,
  (newVal) => {
    if (newVal !== undefined && newVal !== form.values.streetField) {
      form.values.streetField = newVal;
    }
  }
);
watch(
  () => props.postalCodeField,
  (newVal) => {
    if (newVal !== undefined && newVal !== form.values.postalCodeField) {
      form.values.postalCodeField = newVal;
    }
  }
);
watch(
  () => props.neighborhoodField,
  (newVal) => {
    if (newVal !== undefined && newVal !== form.values.neighborhoodField) {
      form.values.neighborhoodField = newVal;
    }
  }
);
watch(
  () => props.regionField,
  (newVal) => {
    if (newVal !== undefined && newVal !== form.values.regionField) {
      form.values.regionField = newVal;
      // Mark as touched so it highlights if there's an error
      form.touched.regionField = true;
      form.validateField("regionField");

      // If there's no error after validating, clear the touched state so it doesn't show highlighted
      nextTick(() => {
        if (!form.errors.regionField) {
          form.touched.regionField = false;
        }
      });
    }
  }
);
watch(
  () => props.cityField,
  (newVal) => {
    if (newVal !== undefined && newVal !== form.values.cityField) {
      form.values.cityField = newVal;
      // Mark as touched so it highlights if there's an error
      form.touched.cityField = true;
      form.validateField("cityField");

      // If there's no error after validating, clear the touched state so it doesn't show highlighted
      nextTick(() => {
        if (!form.errors.cityField) {
          form.touched.cityField = false;
        }
      });
    }
  }
);

// Watcher to reset the form if all props are empty
watch(
  [
    () => props.streetName,
    () => props.postalCodeField,
    () => props.neighborhoodField,
    () => props.cityField,
  ],
  ([street, postal, neighborhood, city]) => {
    if (!street && !postal && !neighborhood && !city) {
      form.resetForm();
    }
  }
);
</script>
<style scoped>
.button-active {
  opacity: 1;
  pointer-events: auto;
}
.floating-btn {
  background-color: white;
  left: 0;
  z-index: 200;
  height: 70px;
  width: 100%;
  position: fixed;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.floating-btn.keyboard-open {
  transform: translateY(0);
}

.container.keyboard-open {
  padding-bottom: 150px;
}

.floating-btn.button-active {
  opacity: 1;
  pointer-events: auto;
  bottom: 0;
}

.floating-btn:not(.button-active) {
  opacity: 0.5;
  pointer-events: none;
}
</style>
